import {ActivityType} from './activity';
import {RaceRankingMethod} from './race';
import {LinkageService} from './tatta-user';

export interface Team {
  id: number; // チームのid partitionKey
  name: string; // チーム名 gsi partitionKey
  activityType: ActivityType; // gsi sortKey
  type: TeamType;
  category: TeamCategory;
  status: TeamStatus;
  aclTeam: TeamAcl; // チームの参加条件。招待制 = closedで、チーム検索の結果には出ない。誰でも参加可の場合は全ての項目がpublicとなる。
  aclPostFeed: TeamPostAcl; // 掲示板投稿権限
  aclPostTeamCourse: TeamPostAcl; // ホームコース追加権限
  aclPostTeamHistory: TeamPostAcl; // 活動実績投稿権限。基本的には0で、UI上は表示しない。 オフィシャルチームのみ設定を変更できる。
  aclCreateIntraEvent: TeamPostAcl; // チーム内イベント作成権限
  aclInviteMember: TeamPostAcl; // メンバー招待権限
  area?: string; // 活動エリア
  updatedAt: number; // 更新日時
  description: string; // チームの紹介文
  webLink?: string; // チームのWebページリンク
  twitterLink?: string; // チームのtwitterリンク
  facebookLink?: string; // チームのfacebookリンク
  instagramLink?: string; // チームのinstagramリンク
  youtubeLink?: string; // チームのyoutubeリンク
  iconUrl: string; // チームのアイコン画像URL
  imageUrl: string; // チームのカバー画像URL
  membership: number; // メンバー数
  capacity: number; // 定員。0の場合は無制限
  eventsTotal: number; // 活動イベント数
  monthlyTotalDistance: number; // 月間距離
  since?: number;
  requiredServices: LinkageService[]; // 参加時に連携が必要なサービス（複数ある場合はor条件）
  eventRequiredServices: LinkageService[]; // チームイベント参加時に連携が必要なサービス（作成時に選択可能）
}

export interface OfficialTeam extends Team {
  paidAccount: TeamPaidAccount;
  paidPlan: TeamPaidPlan;
  paidPlanHistories: TeamPaidPlan[];
  connectedRuntesNums: string[];
  connectedRunnetRaceIds: number[];
  connectedMoshicomIds: number[];
}

export interface TeamPaidPlan {
  additionalCapacity: number; // Team#capacityにこの値を契約時に追加する。一度増えた定員は戻さない（契約切れの場合はsuspendとするため）
  additionalEvents: RaceRankingMethod[]; // 開催可能なイベント種別(suspend状態の場合は無効)
  startedAt: number;
  expiredAt: number; // 期限が切れそうになったら営業全体MLに通知。Xヶ月以上経過する場合は自動でsuspend
  price: number; // アプリ側でのソート順に利用する
}

export interface TeamPaidAccount {
  email: string; // 契約者のログイン用メールアドレス
  name: string; // 契約者の組織名
}

export interface TeamRelatedAccount extends TeamPaidAccount {
  role: TeamMemberRole.admin | TeamMemberRole.ambassador;
}

export enum TeamMemberRole {
  general = 0,
  admin = 1,
  ambassador = 2,
}

export const getTeamMemberRoleName = (role: TeamMemberRole): string => {
  switch (role) {
    case TeamMemberRole.general:
      return 'メンバー';
    case TeamMemberRole.admin:
      return '管理者';
    case TeamMemberRole.ambassador:
      return 'アンバサダー';
  }
}

export enum TeamType {
  general = 0, // ActivityTypeごとに参加可能な一般的なチーム
  official = 1, // ActivityTypeによる制限を受けず、いくつでも参加可能なスポンサー運営のチーム。チーム対抗イベントには参加できない
  certificated = 2, // 一般チームの中で公益性を伴う活動実績があり、審査に通った場合のみ有効とするもの
  officialPrivate = 3, // ActivityTypeによる制限を受けず、いくつでも参加可能なメンバー限定公開のチーム。健康経営企業等で利用。チーム対抗イベントには参加できない
  officialRaceCommunity = 4, // ActivityTypeによる制限を受けず、いくつでも参加可能な大会主催者運営のチーム。チーム対抗イベントには参加できない
}

export const getTeamTypeName = (type: TeamType): string => {
  switch (type) {
    case TeamType.general:
      return '一般チーム';
    case TeamType.official:
      return 'オフィシャルチーム';
    case TeamType.certificated:
      return '認証チーム';
    case TeamType.officialPrivate:
      return 'オフィシャルチーム(メンバー限定公開)';
    case TeamType.officialRaceCommunity:
      return '大会公式コミュニティ';
  }
};

export enum TeamCategory {
  circle = 0,
  student = 1,
  service = 2,
  company = 3,
  association = 4,
  municipality = 5,
  other = 6,
}

export const getTeamCategoryName = (category: TeamCategory): string => {
  switch (category) {
    case TeamCategory.circle:
      return '走友会・サークル';
    case TeamCategory.student:
      return '学生団体';
    case TeamCategory.service:
      return 'サービス・ショップ';
    case TeamCategory.company:
      return '企業';
    case TeamCategory.association:
      return '協会';
    case TeamCategory.municipality:
      return '行政・自治体';
    case TeamCategory.other:
    default:
      return 'その他';
  }
};

export enum TeamStatus {
  active = 0,
  suspended = 1,
  disbanded = 2,
  draft = 3,
}

export const getTeamStatusName = (status: TeamStatus): string => {
  switch (status) {
    case TeamStatus.active:
      return 'アクティブ';
    case TeamStatus.suspended:
      return '休止中';
    case TeamStatus.disbanded:
      return '解散済み';
    case TeamStatus.draft:
      return '下書き（非公開）';
  }
};

export enum TeamAcl {
  everyone = 0,
  invitedOnly = 1,
}

export const getTeamAclName = (acl: TeamAcl): string => {
  switch (acl) {
    case TeamAcl.everyone:
      return '誰でも参加可能';
    case TeamAcl.invitedOnly:
      return '個別に招待';
  }
};

export enum TeamPostAcl {
  member = 0,
  adminOnly = 1,
}

export const getTeamPostAclName = (acl: TeamPostAcl): string => {
  switch (acl) {
    case TeamPostAcl.member:
      return '全メンバー';
    case TeamPostAcl.adminOnly:
      return '管理者のみ';
  }
};
